/**
 * @file <bk-media-gallery>: the product media track (snippets/media-gallery.liquid).
 *
 * The track is a scroll-snap list, so swiping, keyboard scrolling and the reading direction work
 * without JavaScript. This module keeps the "1 / 8" counter and the thumbnails in sync with what
 * is on screen, moves the track from the thumbnails and the arrow buttons, opens the image
 * lightbox (a <bk-dialog>), jumps to the media of the variant selected in the same section
 * (`bk:variant:change`), and loads Shopify's 3D viewer and "View in your space" features only when
 * the gallery holds a 3D model.
 */
import { subscribe, prefersReducedMotion } from '@theme/utils';
import '@theme/dialog';

class BkMediaGallery extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** Pending animation frame of the scroll sync */
  #frame = 0;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  #active = 1;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.#track?.addEventListener('scroll', () => this.#scheduleSync(), { passive: true, signal });
    this.#unsubscribe = subscribe('bk:variant:change', (detail) => this.#handleVariantChange(detail));
    this.#syncArrows();

    // Start on the selected variant's media once the page has its final layout.
    const selected = this.querySelector('[data-media-track] [data-media-selected]');
    if (selected instanceof HTMLElement) {
      const jump = () => requestAnimationFrame(() => this.#goTo(Number(selected.dataset.mediaIndex) || 1, true));
      if (document.readyState === 'complete') jump();
      else window.addEventListener('load', jump, { once: true, signal });
    }

    if (this.querySelector('[data-media-model]')) this.#loadModelFeatures();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    cancelAnimationFrame(this.#frame);
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    const track = this.querySelector('[data-media-track]');
    return track instanceof HTMLElement ? track : null;
  }

  /** @returns {HTMLElement[]} Slides of the track, in DOM order */
  get #items() {
    const track = this.#track;
    if (!track) return [];
    return [...track.children].filter(
      /** @returns {item is HTMLElement} */ (item) => item instanceof HTMLElement && item.hasAttribute('data-media-index'),
    );
  }

  #scheduleSync() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#syncFromScroll();
    });
  }

  /**
   * Every slide is as wide as the track, so the slide on screen follows from the scroll offset
   * (absolute value: right-to-left tracks scroll to negative offsets).
   */
  #syncFromScroll() {
    const track = this.#track;
    const total = this.#items.length;
    if (!track || total < 2) return;
    const index = Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1)) + 1;
    this.#setActive(Math.min(total, Math.max(1, index)));
  }

  /** @param {number} index - 1-based media index now on screen */
  #setActive(index) {
    if (index === this.#active) return;
    this.#active = index;

    const counter = this.querySelector('[data-media-counter]');
    if (counter) {
      // The translated template keeps its separator: only the leading number changes.
      counter.textContent = (counter.textContent ?? '').replace(/\d+/, String(index));
    }

    for (const thumb of this.querySelectorAll('[data-media-go]')) {
      if (Number(thumb.getAttribute('data-media-go')) === index) {
        thumb.setAttribute('aria-current', 'true');
        if (thumb instanceof HTMLElement) this.#revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
    this.#syncArrows();
  }

  /**
   * Keep the active thumbnail visible in its own row without scrolling the page.
   * @param {HTMLElement} thumb
   */
  #revealThumb(thumb) {
    const row = thumb.closest('ul');
    if (!(row instanceof HTMLElement) || row.scrollWidth <= row.clientWidth) return;
    const rowRect = row.getBoundingClientRect();
    const rect = thumb.getBoundingClientRect();
    if (rect.left < rowRect.left || rect.right > rowRect.right) {
      row.scrollBy({ left: rect.left - rowRect.left - (rowRect.width - rect.width) / 2, behavior: 'instant' });
    }
  }

  #syncArrows() {
    const total = this.#items.length;
    const prev = this.querySelector('[data-media-prev]');
    const next = this.querySelector('[data-media-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = this.#active <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = this.#active >= total;
  }

  /**
   * Scroll the track to a slide. Uses the on-screen offset between the slide and the track, so it
   * works the same in left-to-right and right-to-left documents.
   * @param {number} index - 1-based media index
   * @param {boolean} [instant]
   */
  #goTo(index, instant = false) {
    const track = this.#track;
    const item = this.#items.find((candidate) => Number(candidate.dataset.mediaIndex) === index);
    if (!track || !item) return;
    const delta = item.getBoundingClientRect().left - track.getBoundingClientRect().left;
    if (instant || prefersReducedMotion()) {
      track.scrollBy({ left: delta, behavior: 'instant' });
      this.#syncFromScroll();
      return;
    }
    track.scrollBy({ left: delta, behavior: 'smooth' });
    this.#setActive(index);
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const thumb = target.closest('[data-media-go]');
    if (thumb instanceof HTMLElement) {
      this.#goTo(Number(thumb.dataset.mediaGo) || 1);
      return;
    }
    if (target.closest('[data-media-prev]')) {
      this.#goTo(Math.max(1, this.#active - 1));
      return;
    }
    if (target.closest('[data-media-next]')) {
      this.#goTo(Math.min(this.#items.length, this.#active + 1));
      return;
    }
    const zoom = target.closest('[data-media-zoom]');
    if (zoom instanceof HTMLElement) this.#openLightbox(Number(zoom.dataset.mediaZoom) || 1, zoom);
  }

  /**
   * @param {number} index - Media index of the image to show first
   * @param {HTMLElement} trigger - Receives focus again when the lightbox closes
   */
  #openLightbox(index, trigger) {
    const host = /** @type {HTMLElement & { open?: (trigger?: HTMLElement) => void }} */ (this.querySelector('bk-dialog'));
    if (typeof host?.open !== 'function') return;
    host.open(trigger);
    const item = host.querySelector(`[data-lightbox-index="${index}"]`);
    if (item instanceof HTMLElement) {
      item.scrollIntoView({ block: 'start', behavior: 'instant' });
      item.focus({ preventScroll: true });
    }
  }

  /** @param {{ variant?: { featured_media?: { id?: number } | null } | null, sectionId?: string } | undefined} detail */
  #handleVariantChange(detail) {
    if (!detail || (detail.sectionId && detail.sectionId !== this.dataset.sectionId)) return;
    const mediaId = detail.variant?.featured_media?.id;
    if (!mediaId) return;
    const item = this.#items.find((candidate) => candidate.dataset.mediaId === String(mediaId));
    if (item) this.#goTo(Number(item.dataset.mediaIndex) || 1);
  }

  /** Shopify's own model viewer UI and AR quick look, loaded on demand (Shopify.loadFeatures). */
  #loadModelFeatures() {
    const shopify = /** @type {any} */ (window).Shopify;
    if (typeof shopify?.loadFeatures !== 'function') return;

    shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (/** @type {unknown} */ errors) => {
          if (errors) return;
          for (const viewer of this.querySelectorAll('model-viewer')) {
            if (!(viewer instanceof HTMLElement) || viewer.dataset.bkModelUi) continue;
            viewer.dataset.bkModelUi = 'true';
            new shopify.ModelViewerUI(viewer);
          }
        },
      },
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: (/** @type {unknown} */ errors) => {
          if (!errors) this.#setupXr();
        },
      },
    ]);
  }

  #setupXr() {
    const xr = /** @type {any} */ (window).ShopifyXR;
    if (!xr) {
      document.addEventListener('shopify_xr_initialized', () => this.#setupXr(), { once: true });
      return;
    }
    const source = this.querySelector('script[data-product-models]');
    if (source?.textContent) {
      try {
        xr.addModels(JSON.parse(source.textContent));
      } catch {
        return;
      }
      source.remove();
    }
    xr.setupXRElements();
  }
}

if (!customElements.get('bk-media-gallery')) customElements.define('bk-media-gallery', BkMediaGallery);
